// Zod DTOs for the `/v1/students` + `/v1/enrollments` roster surface (spec
// §3.3 `User`/`Enrollment` models, §6.2 "roster/enrollment" — ADR-001:
// ADMIN-exclusive, never TEACHER). Single source of truth for `apps/api`'s
// validators + the OpenAPI generator, mirroring `./level.ts`'s header
// convention for this package.
import { z } from 'zod';
import { emailSchema, idSchema, nameSchema, passwordSchema, paginationQuerySchema } from './primitives';

// ─── POST /v1/students (invite) ─────────────────────────────────────────────
// Issues an INVITED STUDENT row + an `email.invite` VerificationToken —
// mirrors `../jobs/email-messages.ts`'s existing `emailInviteMessageSchema`
// (already shipped, unused until this task) and `POST /v1/auth/register`'s
// shape one section over in `./auth.ts`. `role` is NOT part of this request:
// the roster surface only ever invites STUDENTs (spec §2 "roster" =
// students); inviting a TEACHER/ADMIN co-worker is a separate, out-of-scope
// admin-user-management flow, not this ticket's `/v1/students`.

export const studentInviteRequestSchema = z.object({
  email: emailSchema,
  name: nameSchema,
});
export type StudentInviteRequest = z.infer<typeof studentInviteRequestSchema>;

// ─── POST /v1/students/direct (admin sets initial credentials) ─────────────
// ADMIN-only (never TEACHER — a credential-setting flow, narrower than the
// invite surface's `TEACHER_OR_ADMIN`, see `../../../../apps/api/src/routes/
// students.ts`'s `POST /students/direct` doc comment). Creates an `ACTIVE`
// STUDENT immediately, no invite email — reuses the exact same
// `passwordSchema` as `POST /v1/auth/register`/`/invite/accept` so the
// password policy can't drift between flows.

export const studentDirectCreateRequestSchema = z.object({
  email: emailSchema,
  name: nameSchema,
  password: passwordSchema,
});
export type StudentDirectCreateRequest = z.infer<typeof studentDirectCreateRequestSchema>;

// ─── GET /v1/students ───────────────────────────────────────────────────────

export const studentListQuerySchema = paginationQuerySchema.extend({
  /** Case-insensitive substring match against `email`/`name` — optional. */
  search: z.string().trim().min(1).max(200).optional(),
  /** Optional exact-match filter on `User.status` — additive alongside
   * `search`, combined with AND when both are present. */
  status: z.enum(['ACTIVE', 'INVITED', 'SUSPENDED']).optional(),
});
export type StudentListQuery = z.infer<typeof studentListQuerySchema>;

// ─── POST /v1/students/packages/batch ───────────────────────────────────────
// The multi-student counterpart of `GET /v1/students/:id/packages` — lets
// the admin/teacher Students table fetch a "current package(s)" summary for
// a whole rendered page of rows in ONE round trip instead of one request per
// row (N+1). `studentIds` is bounded to keep a single request's D1
// `IN (...)` clause and the request body itself reasonably sized; the
// caller (a paginated table) only ever needs the ids of the CURRENT page,
// which is far below this ceiling at any realistic page size.
export const studentPackagesBatchRequestSchema = z.object({
  studentIds: z.array(idSchema).min(1).max(200),
});
export type StudentPackagesBatchRequest = z.infer<typeof studentPackagesBatchRequestSchema>;

// ─── PATCH /v1/students/:id ─────────────────────────────────────────────────
// Never accepts `role`/`email` (identity fields — spec has no "change a
// student's login email" flow here) or `status: 'PENDING_VERIFICATION'`
// (a system-only state, never admin-settable) — `status` is restricted to
// the two states an ADMIN may legitimately toggle a roster member between
// post-invite: reinstating a `SUSPENDED` account back to `ACTIVE`, or
// suspending an `ACTIVE`/`INVITED` one. `INVITED` -> `ACTIVE` happens only
// via `/v1/auth/invite/accept` (the student sets their own password), never
// this PATCH.

export const studentUpdateRequestSchema = z
  .object({
    name: nameSchema.optional(),
    status: z.enum(['ACTIVE', 'SUSPENDED']).optional(),
  })
  .refine((value) => value.name !== undefined || value.status !== undefined, {
    message: 'At least one of name or status is required',
  });
export type StudentUpdateRequest = z.infer<typeof studentUpdateRequestSchema>;

// ─── POST /v1/students/:id/enrollments ──────────────────────────────────────

export const enrollmentCreateRequestSchema = z.object({
  levelId: idSchema,
  expiresAt: z.string().datetime({ message: 'expiresAt must be an ISO-8601 datetime' }),
});
export type EnrollmentCreateRequest = z.infer<typeof enrollmentCreateRequestSchema>;

// ─── PATCH /v1/enrollments/:id (extend) ─────────────────────────────────────

export const enrollmentExtendRequestSchema = z.object({
  expiresAt: z.string().datetime({ message: 'expiresAt must be an ISO-8601 datetime' }),
});
export type EnrollmentExtendRequest = z.infer<typeof enrollmentExtendRequestSchema>;

// ─── POST /v1/students/import (CSV, async) ──────────────────────────────────
// The request body itself is raw `text/csv` (not JSON — no zod request
// schema), one row per student: `email,name,levelId,expiresAt` (header row
// required, columns in any order by name). These are the RESPONSE DTOs only
// — `apps/api/src/services/roster.ts`'s `parseRosterCsv` owns the actual row
// parsing/validation, kept out of `packages/shared` since it is D1/Prisma-
// adjacent business logic, not a wire contract.

export const studentImportAcceptedResponseSchema = z.object({
  jobId: z.string().min(1),
  status: z.literal('queued'),
});
export type StudentImportAcceptedResponse = z.infer<typeof studentImportAcceptedResponseSchema>;

export const studentImportRowErrorSchema = z.object({
  row: z.number().int().nonnegative(),
  email: z.string().nullable(),
  message: z.string(),
});
export type StudentImportRowError = z.infer<typeof studentImportRowErrorSchema>;

export const studentImportJobStatusSchema = z.enum([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
]);
export type StudentImportJobStatus = z.infer<typeof studentImportJobStatusSchema>;

export const studentImportJobResponseSchema = z.object({
  jobId: z.string().min(1),
  status: studentImportJobStatusSchema,
  totalRows: z.number().int().nonnegative(),
  succeeded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z.array(studentImportRowErrorSchema),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});
export type StudentImportJobResponse = z.infer<typeof studentImportJobResponseSchema>;
